import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DailyPulse } from '../../api/astrology';
import { getMoonPhase, getMoonSign } from '../../data/sacredCalendar';
import CosmicPulseModal from '../shared/CosmicPulseModal';

/* ─── Swipeable Pulse Cards ─── */
const PulseCarousel: React.FC<{ pulse: DailyPulse }> = ({ pulse }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCosmicPulseModal, setShowCosmicPulseModal] = useState(false);

  const [boostLoading, setBoostLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchDelta = useRef(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Track if Cosmic Pulse was revealed today
  const today = new Date().toISOString().slice(0, 10);
  const cosmicPulseKey = `zeal_cosmic_pulse_revealed_${today}`;
  const cosmicWeatherKey = `zeal_cosmic_pulse_weather_${today}`;
  const hasRevealedToday = localStorage.getItem(cosmicPulseKey) === 'true';
  const weatherSummary = localStorage.getItem(cosmicWeatherKey) || '';

  const handleCosmicPulseOpen = () => {
    setShowCosmicPulseModal(true);
  };

  const currentMoon = getMoonPhase();
  const currentMoonSign = getMoonSign(new Date());

  const timing = pulse.timing;
  const hasTimingBlueprint = timing?.has_blueprint ?? false;
  const hasActiveTransit = hasTimingBlueprint && !!timing?.active_transit;



  const handleTimingClick = () => {
    if (!hasTimingBlueprint) {
      navigate('/reports');
      return;
    }
    navigate('/reports/future');
  };

  // Build timing card sublabel
  const timingSublabel = !hasTimingBlueprint
    ? 'Unlock personalized insights'
    : hasActiveTransit && timing?.active_transit
      ? `${timing.active_transit.transit_planet} ${timing.active_transit.aspect} ${timing.active_transit.natal_target}`
      : 'No major transits today';

  // No stat line on timing card — boosts apply automatically via active-boosts endpoint
  const timingStat = '';

  const cards = [
    {
      key: 'daily-reading',
      icon: '',
      label: hasRevealedToday ? 'Today\'s Reading' : 'Your Daily Reading',
      sublabel: 'Personalized cosmic guidance',
      message: hasRevealedToday && weatherSummary
        ? weatherSummary.slice(0, 140) + (weatherSummary.length > 140 ? '...' : '')
        : 'Tap to reveal today\'s full cosmic reading — your moon energy, planetary transits, and personalized guidance based on your natal chart.',
      accent: 'rgba(201,168,76,0.25)',
      accentBorder: 'rgba(201,168,76,0.5)',
      stat: '',
      heroImage: '/cosmic-pulse-hero.jpg',
      showCTA: !hasRevealedToday,
      onClick: handleCosmicPulseOpen,
    },
    {
      key: 'moon',
      icon: currentMoon.icon,
      label: 'Moon Pulse',
      sublabel: `${currentMoon.phaseName} · ${(pulse.moon as any).sign || currentMoonSign.sign.name}`,
      message: pulse.moon.message,
      accent: 'rgba(201,168,76,0.25)',
      accentBorder: 'rgba(201,168,76,0.35)',
      stat: `${pulse.moon.attribute}: ${pulse.moon.user_score}`,
      heroImage: '/moon-pulse-bg.jpg',
      onClick: () => navigate('/calendar'),
    },
    {
      key: 'transit',
      icon: pulse.transit.planet === 'Mercury' ? '☿' :
            pulse.transit.planet === 'Venus' ? '♀' :
            pulse.transit.planet === 'Mars' ? '♂' :
            pulse.transit.planet === 'Jupiter' ? '♃' :
            pulse.transit.planet === 'Saturn' ? '♄' :
            pulse.transit.planet === 'Uranus' ? '♅' :
            pulse.transit.planet === 'Neptune' ? '♆' :
            pulse.transit.planet === 'Pluto' ? '♇' : '✦',
      label: 'Transit Spotlight',
      sublabel: `${pulse.transit.planet} ${pulse.transit.event === 'retrograde' ? 'Retrograde' : 'in'} ${pulse.transit.sign}`,
      message: pulse.transit.message,
      accent: 'rgba(160,120,255,0.2)',
      accentBorder: 'rgba(160,120,255,0.35)',
      stat: `${pulse.transit.attribute}: ${pulse.transit.user_score}`,
      heroImage: '/transit-pulse-bg.jpg',
      onClick: () => navigate('/calendar#transit-spotlight'),
    },
    {
      key: 'circle',
      icon: pulse.circle.has_member ? '✦' : '👥',
      label: pulse.circle.has_member ? 'Circle Pulse' : 'Your Circle',
      sublabel: pulse.circle.has_member ? pulse.circle.member_name || '' : 'Connect with someone',
      message: pulse.circle.message,
      accent: 'rgba(76,201,168,0.15)',
      accentBorder: 'rgba(76,201,168,0.3)',
      stat: pulse.circle.relationship || '',
      heroImage: '/circle-pulse-bg.jpg',
      onClick: () => navigate('/circle'),
    },
    ...(timing ? [{
      key: 'timing',
      icon: '⧖',
      label: 'Timing Insight',
      sublabel: timingSublabel,
      message: timing.message,
      accent: 'rgba(231,76,139,0.2)',
      heroImage: '/timing-pulse-bg.jpg',
      accentBorder: 'rgba(231,76,139,0.35)',
      stat: timingStat,
      onClick: handleTimingClick,
    }] : []),
  ];

  // Auto-rotate every 5 seconds, pause on touch
  const autoRotateRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startAutoRotate = useCallback(() => {
    if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    autoRotateRef.current = setInterval(() => {
      setActiveIndex(i => (i + 1) % cards.length);
    }, 8000);
  }, [cards.length]);

  useEffect(() => {
    startAutoRotate();
    return () => { if (autoRotateRef.current) clearInterval(autoRotateRef.current); };
  }, [startAutoRotate]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    touchStartX.current = e.touches[0].clientX;
    touchDelta.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDelta.current = e.touches[0].clientX - touchStartX.current;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const threshold = 50;
    if (touchDelta.current < -threshold && activeIndex < cards.length - 1) {
      setActiveIndex(i => i + 1);
    } else if (touchDelta.current > threshold && activeIndex > 0) {
      setActiveIndex(i => i - 1);
    }
    touchDelta.current = 0;
    startAutoRotate();
  }, [activeIndex, cards.length, startAutoRotate]);

  return (
    <div style={{ marginBottom: '24px' }}>
      <div
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="glow-pulse"
        style={{ overflow: 'hidden', borderRadius: '18px', padding: '3px' }}
      >
        <div style={{
          display: 'flex',
          transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: `translateX(-${activeIndex * 100}%)`,
          alignItems: 'stretch',
        }}>
          {cards.map((card: any) => (
            <div key={card.key} onClick={card.onClick} style={{ minWidth: '100%', boxSizing: 'border-box', padding: '0 2px', cursor: 'pointer', display: 'flex' }}>
              {card.heroImage ? (
                <div style={{
                  borderRadius: '16px', minHeight: '200px', width: '100%', position: 'relative', overflow: 'hidden',
                  border: `1px solid ${card.accentBorder}`,
                }}>
                  <img src={card.heroImage} alt="Daily Reading" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.7) 70%, rgba(0,0,0,0.85) 100%)' }} />
                  <div style={{ position: 'relative', zIndex: 1, padding: '24px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: '200px' }}>
                    <p style={{ fontSize: '11px', letterSpacing: '2px', color: 'rgba(201,168,76,0.9)', textTransform: 'uppercase', margin: '0 0 6px', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{card.sublabel}</p>
                    <h3 style={{ fontFamily: 'Cinzel, serif', fontSize: '22px', fontWeight: 600, color: '#fff', margin: '0 0 10px', textShadow: '0 2px 8px rgba(0,0,0,0.5)', lineHeight: 1.2 }}>{card.label}</h3>
                    <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5, margin: 0 }}>{card.message}</p>
                    {card.showCTA && (
                      <div style={{ marginTop: '14px' }}>
                        <span style={{ fontSize: '12px', padding: '6px 16px', borderRadius: '16px', background: 'rgba(201,168,76,0.2)', border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c', fontWeight: 600, letterSpacing: '1px' }}>Reveal Reading ✦</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
              <div style={{
                background: `linear-gradient(135deg, ${card.accent} 0%, rgba(10,10,15,0.95) 100%)`,
                border: `1px solid ${card.accentBorder}`,
                borderRadius: '16px', padding: '24px 20px', minHeight: '200px',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                width: '100%',
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '28px' }}>{card.icon}</span>
                    <div>
                      <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', margin: 0 }}>{card.label}</p>
                      <p style={{ fontSize: '15px', color: '#c9a84c', fontWeight: 600, margin: '2px 0 0' }}>{card.sublabel}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: '16px', fontStyle: 'italic', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: 0, display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}>
                    "{card.message}"
                  </p>
                </div>
                {card.stat && (
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '14px', textTransform: 'capitalize' }}>{card.stat}</p>
                )}
              </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '12px' }}>
        {cards.map((card, i) => (
          <button key={card.key} onClick={() => setActiveIndex(i)} style={{
            width: i === activeIndex ? '20px' : '6px', height: '6px', borderRadius: '3px', border: 'none',
            background: i === activeIndex ? '#4a9eed' : 'rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease', cursor: 'pointer', padding: 0,
          }} aria-label={`Pulse card ${i + 1}`} />
        ))}
      </div>
      {showCosmicPulseModal && <CosmicPulseModal onClose={() => setShowCosmicPulseModal(false)} />}
    </div>
  );
};

export default PulseCarousel;
